import { useState, forwardRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Briefcase, Users, Loader2 } from '@digital-twins/design-system/icons'
import { Heading } from '@/components/ui/Heading'
import { Text } from '@/components/ui/Text'
import { Button } from '@/components/ui/button'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { usersApi } from '@/lib/api'
import { OnboardingStepProps, TargetingType } from '@/types/components/onboarding'
import { FadeUp } from '../FadeUp'

type SelectableType = 'B2B' | 'B2C'

interface TargetingOption {
  type: SelectableType
  icon: React.ElementType
}

const TARGETING_OPTIONS: Array<TargetingOption> = [
  {
    type: 'B2B',
    icon: Briefcase,
  },
  {
    type: 'B2C',
    icon: Users,
  },
]

interface TargetingCardProps {
  option: TargetingOption
  isSelected: boolean
  isSaving: boolean
  onToggle: () => void
}

function TargetingCard({ option, isSelected, isSaving, onToggle }: TargetingCardProps) {
  const { t } = useTranslation('onboarding')
  const { type, icon: Icon } = option
  const label = type === 'B2B' ? t('audience.b2b') : t('audience.b2c')

  return (
    <button
      role="checkbox"
      aria-checked={isSelected}
      data-testid={`audience-targeting-${type.toLowerCase()}`}
      onClick={onToggle}
      disabled={isSaving}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onToggle()
        }
      }}
      className={cn(
        'flex flex-1 flex-col items-center gap-2 rounded-xl border-2 px-4 py-5 transition-colors',
        'disabled:pointer-events-none disabled:opacity-50',
        isSelected
          ? 'border-primary bg-primary/5'
          : 'border-border bg-background hover:border-muted-foreground/30 hover:bg-muted/50',
      )}
    >
      <Icon
        className={cn(
          'h-6 w-6',
          isSelected ? 'text-primary' : 'text-muted-foreground',
        )}
      />
      <Text size="sm" weight="medium">
        {label}
      </Text>
    </button>
  )
}

export const AudienceStep = forwardRef<HTMLDivElement, OnboardingStepProps>(
  function AudienceStep({ onUpdateState, onNext, className }, ref) {
    const { t } = useTranslation(['onboarding', 'common'])
    const [selected, setSelected] = useState<Set<SelectableType>>(new Set())
    const [isSaving, setIsSaving] = useState(false)

    function toggleType(type: SelectableType) {
      setSelected((prev) => {
        const next = new Set(prev)
        if (next.has(type)) {
          next.delete(type)
        } else {
          next.add(type)
        }
        return next
      })
    }

    function resolveTargetingType(): TargetingType {
      if (selected.has('B2B') && selected.has('B2C')) return 'BOTH'
      if (selected.has('B2B')) return 'B2B'
      return 'B2C'
    }

    async function handleContinue() {
      const type = resolveTargetingType()
      setIsSaving(true)
      try {
        await usersApi.updateTargetingPreferences({ targetingType: type })
        onUpdateState({ targetingType: type })
        onNext()
      } catch {
        toast.warning(t('audience.saveWarning'))
        onUpdateState({ targetingType: type })
        onNext()
      } finally {
        setIsSaving(false)
      }
    }

    const hasSelection = selected.size > 0

    return (
      <div ref={ref} tabIndex={-1} className={cn('flex flex-col outline-none', className)}>
        {/* Heading */}
        <FadeUp delay={0} className="text-center">
          <Heading level={1} size="4xl">
            {t('audience.title')}
          </Heading>
          <Text size="sm" color="muted" className="mt-2">
            {t('audience.subtitle')}
          </Text>
        </FadeUp>

        {/* Targeting cards */}
        <FadeUp delay={120} className="mt-6">
          <div
            role="group"
            aria-label={t('audience.aria')}
            className="flex gap-3"
            data-testid="audience-targeting-selector"
          >
            {TARGETING_OPTIONS.map((option) => (
              <TargetingCard
                key={option.type}
                option={option}
                isSelected={selected.has(option.type)}
                isSaving={isSaving}
                onToggle={() => toggleType(option.type)}
              />
            ))}
          </div>
        </FadeUp>

        {/* Continue stays mounted so we can animate height (fr grid) instead of mounting FadeUp + snapping layout */}
        <div
          aria-hidden={!hasSelection}
          className={cn(
            'grid overflow-hidden motion-reduce:transition-none',
            'transition-[grid-template-rows,margin-top] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]',
            hasSelection ? 'mt-6 grid-rows-[1fr]' : 'pointer-events-none mt-0 grid-rows-[0fr]',
          )}
        >
          <div className="min-h-0 overflow-hidden">
            <Button
              data-testid="audience-continue-button"
              variant="cta"
              onClick={handleContinue}
              disabled={isSaving || !hasSelection}
              className="w-full"
              tabIndex={hasSelection ? 0 : -1}
            >
              {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : t('continue', { ns: 'common' })}
            </Button>
          </div>
        </div>
      </div>
    )
  },
)
